import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  GitPullRequestArrow,
  Plus,
  Check,
  X,
  FileText,
  Wallet,
  CalendarClock,
  Users,
  ArrowRight,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { formatCurrency } from '@/lib/utils/currency';
import { useAuth } from '@/features/auth';
import { useMatterChangeRequests, useChangeRequestMutations } from '../hooks';
import { CR_TYPE_VALUES } from '../types';
import type { ChangeRequest, CrType, Matter } from '../types';

const NONE = 'NONE';

interface ChangeRequestsCardProps {
  matter: Matter;
}

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'border-amber-300 bg-amber-50 text-amber-700',
  APPROVED: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  REJECTED: 'border-red-300 bg-red-50 text-red-700',
};

/** Per-type icon + tint for the CR rows. */
const TYPE_META: Record<CrType, { icon: React.ElementType; chip: string }> = {
  SCOPE: { icon: FileText, chip: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  BUDGET: { icon: Wallet, chip: 'bg-amber-50 text-amber-600 border-amber-200' },
  TIMELINE: { icon: CalendarClock, chip: 'bg-sky-50 text-sky-600 border-sky-200' },
  TEAM: { icon: Users, chip: 'bg-violet-50 text-violet-600 border-violet-200' },
};

/**
 * Case 360° (Lot 2) Stage 11 — Change Requests: the only way to amend the
 * Matter Plan after Gate 2 (the lifecycle never goes backward). Lead/Partner/MD
 * submit a typed CR (SCOPE / BUDGET / TIMELINE / TEAM); the matter Partner or
 * an MD approves (the target is applied only then) or rejects with a reason.
 */
export function ChangeRequestsCard({ matter }: ChangeRequestsCardProps) {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuth();
  const { data: changeRequests, isLoading } = useMatterChangeRequests(matter?.id);
  const { createCrMutation, approveCrMutation, rejectCrMutation } =
    useChangeRequestMutations();

  // Optimistic client-side gates — the server re-checks everything.
  const globalRoleType = user?.role?.type;
  const isTeamPartner =
    matter?.partners?.some((p) => p?.id === user?.id) ?? false;
  const isTeamLead =
    matter?.responsibleAttorneys?.some((a) => a?.id === user?.id) ?? false;
  const isCrPhase = matter?.phase === 'EXECUTION' || matter?.phase === 'REVIEW';
  // `matter:manage-all` = firm-wide case-management scope (Office Manager);
  // the server-side gates accept it alongside the senior role types.
  const hasManageAll = hasAccess('matter', 'manage-all');
  const canCreate =
    hasAccess('matter', 'cr') &&
    isCrPhase &&
    (globalRoleType === 'MD' ||
      globalRoleType === 'MANAGER' ||
      hasManageAll ||
      isTeamPartner ||
      isTeamLead);
  const canDecide =
    hasAccess('matter', 'cr-approve') &&
    (globalRoleType === 'MD' ||
      globalRoleType === 'MANAGER' ||
      hasManageAll ||
      isTeamPartner);

  // Pending requests need eyes first; within each group keep newest-first
  // (the server already orders by createdAt desc; the sort is stable).
  const sortedRequests = React.useMemo(
    () =>
      [...(changeRequests ?? [])].sort(
        (a, b) =>
          (a?.status === 'PENDING' ? 0 : 1) - (b?.status === 'PENDING' ? 0 : 1),
      ),
    [changeRequests],
  );
  const pendingCount =
    changeRequests?.filter((cr) => cr?.status === 'PENDING')?.length ?? 0;

  // --- create dialog ---
  const [addOpen, setAddOpen] = React.useState(false);
  const [type, setType] = React.useState<CrType>('SCOPE');
  const [scopeIn, setScopeIn] = React.useState('');
  const [scopeOut, setScopeOut] = React.useState('');
  const [budget, setBudget] = React.useState('');
  const [targetEndDate, setTargetEndDate] = React.useState('');
  const [addUserId, setAddUserId] = React.useState<number | undefined>(undefined);
  const [removeUserId, setRemoveUserId] = React.useState<string>(NONE);
  const [reason, setReason] = React.useState('');

  // --- reject dialog ---
  const [rejecting, setRejecting] = React.useState<ChangeRequest | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');

  const openAdd = () => {
    setType('SCOPE');
    setScopeIn(matter?.scopeIn ?? '');
    setScopeOut(matter?.scopeOut ?? '');
    setBudget(matter?.budget != null ? String(matter.budget) : '');
    setTargetEndDate(
      matter?.targetEndDate
        ? format(new Date(matter.targetEndDate), 'yyyy-MM-dd')
        : '',
    );
    setAddUserId(undefined);
    setRemoveUserId(NONE);
    setReason('');
    setAddOpen(true);
  };

  const buildTargetValue = (): string | null => {
    switch (type) {
      case 'SCOPE':
        if (!scopeIn.trim() && !scopeOut.trim()) return null;
        return JSON.stringify({
          ...(scopeIn.trim() ? { scopeIn: scopeIn.trim() } : {}),
          ...(scopeOut.trim() ? { scopeOut: scopeOut.trim() } : {}),
        });
      case 'BUDGET': {
        const value = Number(budget);
        if (budget === '' || !Number.isInteger(value) || value < 0) return null;
        return String(value);
      }
      case 'TIMELINE':
        if (!targetEndDate) return null;
        return new Date(targetEndDate).toISOString();
      case 'TEAM': {
        const add = addUserId ? [addUserId] : [];
        const remove = removeUserId !== NONE ? [Number(removeUserId)] : [];
        if (!add.length && !remove.length) return null;
        return JSON.stringify({
          ...(add.length ? { add } : {}),
          ...(remove.length ? { remove } : {}),
        });
      }
      default:
        return null;
    }
  };

  const targetValue = buildTargetValue();
  const canSubmit =
    !createCrMutation.isPending && !!targetValue && !!reason.trim();

  const handleSubmit = async () => {
    if (!canSubmit || !matter || !targetValue) return;
    await createCrMutation.mutateAsync({
      id: matter.id,
      data: { type, targetValue, reason: reason.trim() },
    });
    setAddOpen(false);
  };

  const handleReject = async () => {
    if (!rejecting || !rejectReason.trim() || rejectCrMutation.isPending) return;
    await rejectCrMutation.mutateAsync({
      crId: rejecting.id,
      matterId: matter?.id ?? 0,
      rejectReason: rejectReason.trim(),
    });
    setRejecting(null);
    setRejectReason('');
  };

  /** Human-readable rendering of a CR value (target or previous snapshot). */
  const renderValue = (crType: CrType, raw: string | null | undefined): string => {
    if (raw == null || raw === '') return '';
    try {
      switch (crType) {
        case 'SCOPE': {
          const v = JSON.parse(raw) as { scopeIn?: string; scopeOut?: string };
          return [
            v?.scopeIn ? `${t('plan.scopeIn')}: ${v.scopeIn}` : null,
            v?.scopeOut ? `${t('plan.scopeOut')}: ${v.scopeOut}` : null,
          ]
            .filter(Boolean)
            .join(' · ');
        }
        case 'BUDGET':
          // The budget (billing cap) is in the contract's currency.
          return formatCurrency(Number(raw), {
            currency: matter?.contract?.currency ?? 'USD',
          });
        case 'TIMELINE':
          return format(new Date(raw), 'dd MMM yyyy');
        case 'TEAM': {
          const v = JSON.parse(raw) as { add?: number[]; remove?: number[] };
          return [
            v?.add?.length ? `${t('cr.teamAdd')}: #${v.add.join(', #')}` : null,
            v?.remove?.length
              ? `${t('cr.teamRemove')}: #${v.remove.join(', #')}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ');
        }
        default:
          return raw;
      }
    } catch {
      return raw;
    }
  };

  /** For single-value types show an "old → new" diff when the snapshot exists. */
  const renderDiff = (cr: ChangeRequest): React.ReactNode => {
    const current = renderValue(cr?.type, cr?.targetValue);
    const showDiff =
      (cr?.type === 'BUDGET' || cr?.type === 'TIMELINE') && !!cr?.previousValue;
    if (!showDiff) {
      return <span className="font-medium text-slate-800">{current}</span>;
    }
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="text-slate-400 line-through">
          {renderValue(cr?.type, cr?.previousValue)}
        </span>
        <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
        <span className="font-medium text-slate-800">{current}</span>
      </span>
    );
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <GitPullRequestArrow className="h-5 w-5 text-slate-400" />
            {t('cr.cardTitle')}
            {pendingCount > 0 && (
              <Badge className="border border-amber-300 bg-amber-50 text-amber-700">
                {t('cr.pendingChip', { count: pendingCount })}
              </Badge>
            )}
          </span>
          {canCreate && (
            <Button
              size="sm"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={openAdd}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('cr.add')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-slate-500">{t('loading')}</p>
        ) : !sortedRequests.length ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <GitPullRequestArrow className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">{t('cr.empty')}</p>
            {canCreate && (
              <Button size="sm" variant="outline" onClick={openAdd}>
                <Plus className="mr-1 h-4 w-4" />
                {t('cr.add')}
              </Button>
            )}
          </div>
        ) : (
          <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {sortedRequests.map((cr) => {
              const meta = TYPE_META[cr?.type ?? 'SCOPE'] ?? TYPE_META.SCOPE;
              const TypeIcon = meta.icon;
              const isCrPending = cr?.status === 'PENDING';
              return (
                <li
                  key={cr.id}
                  className={`rounded-lg border px-3 py-2.5 text-sm ${
                    isCrPending
                      ? 'border-amber-200 border-l-2 border-l-amber-400 bg-amber-50/40'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${meta.chip}`}
                    >
                      <TypeIcon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center justify-between gap-1.5">
                        <span className="flex items-center gap-2 font-medium text-slate-800">
                          {t(`cr.type.${cr?.type ?? 'SCOPE'}`)}
                          <Badge
                            className={`border ${STATUS_BADGE[cr?.status ?? 'PENDING']}`}
                          >
                            {t(`cr.status.${cr?.status ?? 'PENDING'}`)}
                          </Badge>
                        </span>
                        <span className="text-xs text-slate-400">
                          {cr?.createdAt
                            ? format(new Date(cr.createdAt), 'dd MMM yyyy HH:mm')
                            : ''}
                        </span>
                      </div>

                      <p className="break-words text-xs text-slate-700">
                        {renderDiff(cr)}
                      </p>

                      {cr?.reason && (
                        <p className="break-words border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">
                          {cr.reason}
                        </p>
                      )}

                      <p className="text-xs text-slate-400">
                        {cr?.author?.name ?? `#${cr?.authorId}`}
                        {cr?.status !== 'PENDING' && cr?.approver?.name && (
                          <>
                            {' · '}
                            {t('cr.decidedBy')}: {cr.approver.name}
                          </>
                        )}
                      </p>

                      {cr?.status === 'REJECTED' && cr?.rejectReason && (
                        <p className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-600">
                          {t('cr.rejectedReason')}: {cr.rejectReason}
                        </p>
                      )}

                      {isCrPending && canDecide && (
                        <div className="flex items-center justify-end gap-1.5 pt-1">
                          <Button
                            size="sm"
                            className="h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700"
                            disabled={approveCrMutation.isPending}
                            onClick={() =>
                              approveCrMutation.mutate({
                                crId: cr.id,
                                matterId: matter?.id ?? 0,
                              })
                            }
                          >
                            <Check className="mr-1 h-3.5 w-3.5" />
                            {t('cr.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-red-300 px-2.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => {
                              setRejectReason('');
                              setRejecting(cr);
                            }}
                          >
                            <X className="mr-1 h-3.5 w-3.5" />
                            {t('cr.reject')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* create dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitPullRequestArrow className="h-4 w-4 text-slate-500" />
              {t('cr.addDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('cr.addDialogDescription')}</DialogDescription>
          </DialogHeader>

          <div className="my-2 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('cr.typeLabel')}
              </label>
              <Select value={type} onValueChange={(v) => setType(v as CrType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CR_TYPE_VALUES.map((ct) => (
                    <SelectItem key={ct} value={ct}>
                      {t(`cr.type.${ct}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {type === 'SCOPE' && (
              <>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('plan.scopeIn')}
                  </label>
                  <Textarea
                    value={scopeIn}
                    onChange={(e) => setScopeIn(e.target.value)}
                    rows={2}
                    maxLength={5000}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('plan.scopeOut')}
                  </label>
                  <Textarea
                    value={scopeOut}
                    onChange={(e) => setScopeOut(e.target.value)}
                    rows={2}
                    maxLength={5000}
                  />
                </div>
              </>
            )}

            {type === 'BUDGET' && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('cr.newBudget')} ({matter?.contract?.currency ?? 'USD'})
                </label>
                <Input
                  type="number"
                  min={0}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                />
              </div>
            )}

            {type === 'TIMELINE' && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('cr.newTargetEndDate')}
                </label>
                <DateTimePicker
                  mode="date"
                  value={targetEndDate || undefined}
                  onChange={(d) =>
                    setTargetEndDate(d ? format(d, 'yyyy-MM-dd') : '')
                  }
                />
              </div>
            )}

            {type === 'TEAM' && (
              <>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('cr.teamAddLabel')}
                  </label>
                  <UniversalCombobox
                    url="/users"
                    listUrl="/users/select-box"
                    labelKey="name"
                    valueKey="id"
                    selectedId={addUserId}
                    onSelectItem={(item) =>
                      setAddUserId(item?.['id'] as number | undefined)
                    }
                    placeholder={t('selectUser')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('cr.teamRemoveLabel')}
                  </label>
                  <Select value={removeUserId} onValueChange={setRemoveUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('cr.teamRemovePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('cr.teamNoRemove')}</SelectItem>
                      {(matter?.staffs ?? []).map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s?.name} {s?.lastname ?? ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-400">{t('cr.teamHint')}</p>
              </>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('cr.reasonLabel')}
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('cr.reasonPlaceholder')}
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              disabled={createCrMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {createCrMutation.isPending ? t('processing') : t('cr.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reject dialog */}
      <Dialog
        open={!!rejecting}
        onOpenChange={(open) => {
          if (!open) setRejecting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cr.rejectDialogTitle')}</DialogTitle>
            <DialogDescription>{t('cr.rejectDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="my-2">
            <label className="mb-2 block text-sm font-medium">
              {t('cr.reasonLabel')}
            </label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejecting(null)}
              disabled={rejectCrMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectCrMutation.isPending || !rejectReason.trim()}
            >
              {rejectCrMutation.isPending ? t('processing') : t('cr.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
